import React, { useState, useRef, useEffect, useMemo } from "react";
import { SERVER_URL } from "../config";
import "../styles/tableViewport.css";
import { useViewportFit, TABLE_VIEWPORT } from "../hooks/useViewportFit";
import { calculateOvalSeatLayout, getPlayerCountSeatScale, getVerticalSeatBoost, getDesktopOrbitClearance, getDesktopPlayAreaInsets, resolveDesktopPlayAreaInsets, getSeatDepthZ, readLayoutOverrides } from "../utils/seatPositioning";
import { buildViewerPaymentSummary } from "../utils/paymentSettlement";
import { openGameHelp } from "../utils/openGameHelp";

const PHASE_LABELS = {
  waiting: "Waiting for players...",
  suitvote: "Vote for Dealer Suit",
  dealer_reveal: "Finding Dealer...",
  dealer_selected: "Dealer Selected",
  deal: "Dealing cards...",
  action: "Deciding knock/discard...",
  reveal: "Revealing knocked hands...",
  results: "Hand Complete!",
};

const suitSymbols = { spades: "♠", hearts: "♥", diamonds: "♦", clubs: "♣" };
const suitColors = { spades: "#333", clubs: "#333", hearts: "#d22", diamonds: "#d22" };
const SUIT_DISPLAY = { spades: "Spades", hearts: "Hearts", diamonds: "Diamonds", clubs: "Clubs" };
const SUIT_KEYS = ["spades", "hearts", "diamonds", "clubs"];
const SEAT = {
  opponentW: 122,
  myW: 167,
  cardW: 34,
  cardH: 47,
  cardWMe: 40,
  cardHMe: 55,
};

const TABLE_FELT = { width: 536, height: 291, border: 8 };

const CASINO = {
  feltBorder: "#065f46",
  gold: "#d4af37",
  goldDim: "#a16207",
  seatOpp: "linear-gradient(165deg, #1f2937 0%, #111827 100%)",
  seatMe: "linear-gradient(165deg, #2a3d2a 0%, #1a2e1a 100%)",
  seatWin: "linear-gradient(165deg, #3d2f0a 0%, #292006 100%)",
  text: "#f3f4f6",
  textMuted: "#9ca3af",
  cardBack: "linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)",
};

const UI = {
  pagePad: 8,
  headerPad: "8px 10px",
  title: "0.95rem",
  meta: "0.72rem",
  chipPad: "2px 7px",
  btnPad: "4px 9px",
  btnFont: "0.72rem",
  radius: 8,
};

// Modern design system
const COLORS = {
  primary: "#4f46e5",
  primaryHover: "#4338ca",
  secondary: "#64748b",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  background: "#f8fafc",
  surface: "#ffffff",
  border: "#e2e8f0",
  text: "#1e293b",
  textLight: "#64748b",
  textMuted: "#94a3b8",
};

const SHADOWS = {
  sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
  md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
  lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
};

const ANIMATIONS = {
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  hover: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
};

export default function Table({ table, player, session, socket, onTakeBreak, onQuit }) {
  const [chatMsg, setChatMsg] = useState("");
  const [historyTab, setHistoryTab] = useState(false);
  const [dealerReadyClicked, setDealerReadyClicked] = useState(false);
  const [dealerHighlight, setDealerHighlight] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [tempMessage, setTempMessage] = useState(null);
  const [selectedAnteAmount, setSelectedAnteAmount] = useState(2);
  const chatListEnd = useRef(null);
  const historyRef = useRef(null);
  const headerRef = useRef(null);
  const tabsRef = useRef(null);
  const phaseBannerRef = useRef(null);

  // Active seats keep hand indices; pending late joiners are appended for presence only.
  const activeSeats = useMemo(() => (table?.players || []).concat(table?.bots || []), [table]);
  const pendingPlayers = table?.pendingPlayers || [];
  const allSeats = useMemo(() => activeSeats.concat(pendingPlayers), [activeSeats, pendingPlayers]);
  const localPendingPlayer = pendingPlayers.find((p) => p.playerID === player?.playerID) || null;
  const state = table?.state ?? {};
  const phase = state.phase;
  const dealerIdx = state.dealerIdx;
  const dealerSuit = state.dealerSuit;
  const hands = state.hands || [];
  const activePlayerIdx = state.activePlayerIdx;
  const knocked = state.knocked || [];
  const discarded = state.discarded || [];
  const revealOrder = state.revealOrder || [];
  const revealedCount = state.revealedCount || 0;
  const winnerIdx = state.winnerIdx;
  const readies = state.readies || [];
  const readyPlayers = state.readyPlayers || [];
  const leavingNotification = state.leavingNotification;
  const suitVotes = state.suitVotes || [];
  const historyArr = state.history || [];
  const round = state.round;
  const dealerRevealDeck = useMemo(() => state.dealerRevealDeck || [], [state.dealerRevealDeck]);
  const dealerRevealIdx = state.dealerRevealIdx;
  const dealerRevealFound = state.dealerRevealFound;
  const dealerRevealDone = state.dealerRevealDone;
  const dealerRevealDisplayUntil = state.dealerRevealDisplayUntil || 0;
  const isDealerRevealPhase = phase === "dealer_reveal" || phase === "dealer_selected";
  const chat = useMemo(() => (table && table.chat ? table.chat : []), [table, table?.chat]);
  const spectators = useMemo(() => (state && state.spectators ? state.spectators : []), [state, state?.spectators]);
  
             // Ante and Credit System
  const credits = state.credits || {};
  const pot = state.pot || 0;
  const anteCollected = state.anteCollected || false;
  const paymentHistory = useMemo(() => state.paymentHistory || [], [state, state?.paymentHistory]);
  const freeHands = state.freeHands || {};
  const creditValue = state.creditValue || 1.00;
  const [paymentTab, setPaymentTab] = useState(false);
  const activeTab = historyTab ? "history" : paymentTab ? "payment" : "table";
  const { scale: tableScale, design: tableDesign, viewportWidth, availHeight } = useViewportFit({
    headerRef,
    tabsRef,
    phaseBannerRef,
    chatOpen: showChat,
    activeTab,
  });
         
         // Ante Request Voting System
         const anteRequest = state.anteRequest || null;
         const anteVotes = state.anteVotes || {};
         const anteVoteComplete = state.anteVoteComplete || false;
         const anteVoteResult = state.anteVoteResult || null;
         
         // Credit Increase Request System
         const creditIncreaseRequest = state.creditIncreaseRequest || null;
         const creditIncreaseVotes = state.creditIncreaseVotes || {};
         const creditIncreaseVoteComplete = state.creditIncreaseVoteComplete || false;
         const creditIncreaseVoteResult = state.creditIncreaseVoteResult || null;

  const calculatePaymentTracking = useMemo(() => {
    if (!player?.playerID || !paymentHistory?.length || !allSeats?.length) return [];
    return buildViewerPaymentSummary(allSeats, paymentHistory, player.playerID);
  }, [paymentHistory, player?.playerID, allSeats]);

  const myIdx = allSeats.findIndex((p) => p.playerID === player?.playerID);
  const isHost = activeSeats.length > 0 && activeSeats[0]?.playerID === player?.playerID;
  const isSpectate = !!player?.isSpectator;
  const isMobileViewport = viewportWidth <= 768;

  const [layoutOverrideRevision, setLayoutOverrideRevision] = useState(0);
  useEffect(() => {
    const bump = () => setLayoutOverrideRevision((rev) => rev + 1);
    window.addEventListener("gutzLayoutOverridesChanged", bump);
    const onStorage = (event) => {
      if (event.key === "gutzLayoutOverrides" || event.key === null) bump();
    };
    window.addEventListener("storage", onStorage);
    let channel;
    try {
      channel = new BroadcastChannel("gutz-layout");
      channel.onmessage = bump;
    } catch {
      /* BroadcastChannel unavailable */
    }
    return () => {
      window.removeEventListener("gutzLayoutOverridesChanged", bump);
      window.removeEventListener("storage", onStorage);
      if (channel) channel.close();
    };
  }, []);

  const layoutOverrides = useMemo(
    () => readLayoutOverrides(),
    [layoutOverrideRevision]
  );
  const hasLayoutOverrides = !!(layoutOverrides && !isMobileViewport);

  const orbitClearance = useMemo(
    () => getDesktopOrbitClearance(allSeats.length, { isMobile: isMobileViewport }),
    [allSeats.length, isMobileViewport]
  );

  const playAreaInsets = useMemo(
    () => getDesktopPlayAreaInsets({ isMobile: isMobileViewport, totalSeats: allSeats.length }),
    [isMobileViewport, allSeats.length]
  );

  const seatScale = useMemo(() => {
    const countScale = getPlayerCountSeatScale(allSeats.length, {
      isMobile: isMobileViewport,
      overrides: layoutOverrides,
    });
    if (hasLayoutOverrides) return countScale;
    const vertScale = getVerticalSeatBoost(availHeight, isMobileViewport);
    return countScale * vertScale;
  }, [allSeats.length, isMobileViewport, availHeight, layoutOverrides, hasLayoutOverrides]);

  const layoutFitScale = useMemo(() => {
    if (isMobileViewport || allSeats.length < 4) return 1;
    if (allSeats.length >= 6) return 0.94;
    if (allSeats.length >= 5) return 0.97;
    return 1;
  }, [allSeats.length, isMobileViewport]);

  const seatDims = useMemo(() => ({
    opponentW: SEAT.opponentW * seatScale,
    myW: SEAT.myW * seatScale,
    cardW: SEAT.cardW * seatScale,
    cardH: SEAT.cardH * seatScale,
    cardWMe: SEAT.cardWMe * seatScale,
    cardHMe: SEAT.cardHMe * seatScale,
    baseH: 193 * seatScale,
    fontMe: Math.max(0.62, 0.8 * seatScale),
    fontOpp: Math.max(0.55, 0.69 * seatScale),
    fontSuitMe: Math.max(0.72, 0.95 * seatScale),
    fontSuitOpp: Math.max(0.62, 0.8 * seatScale),
  }), [seatScale]);

  const mobileLayoutOverrides = isMobileViewport ? null : layoutOverrides;

  const layoutBaseOpts = useMemo(() => ({
    totalSeats: allSeats.length,
    localPlayerIndex: myIdx,
    tableWidth: TABLE_FELT.width,
    tableHeight: TABLE_FELT.height,
    tableBorder: TABLE_FELT.border,
    containerWidth: tableDesign.width,
    containerHeight: tableDesign.height,
    maxSeatWidth: seatDims.myW,
    maxSeatHeight: seatDims.baseH,
    visualScaleMe: mobileLayoutOverrides?.visualScaleMe ?? (isMobileViewport ? 1.0 : 1.04),
    baseClearance: orbitClearance.baseClearance,
    clearancePerSeat: orbitClearance.clearancePerSeat,
    railGap: mobileLayoutOverrides?.railGap ?? orbitClearance.railGap,
    viewportWidth: mobileLayoutOverrides?.vpW ?? viewportWidth,
    viewportHeight: mobileLayoutOverrides?.vpH ?? availHeight,
    viewportScale: tableScale * layoutFitScale,
    edgeOverflowTop: mobileLayoutOverrides?.overflowT ?? (isMobileViewport ? 28 : 16),
    edgeOverflowBottom: mobileLayoutOverrides?.overflowB ?? (isMobileViewport ? 32 : 5),
    isMobile: isMobileViewport,
  }), [allSeats.length, myIdx, tableDesign.width, tableDesign.height, seatDims, orbitClearance, viewportWidth, availHeight, tableScale, layoutFitScale, isMobileViewport, mobileLayoutOverrides]);

  const { seatLayout, effectivePlayInsets } = useMemo(() => {
    const insets = isMobileViewport
      ? playAreaInsets
      : resolveDesktopPlayAreaInsets(layoutBaseOpts, { isMobile: false, overrides: layoutOverrides });
    return {
      seatLayout: calculateOvalSeatLayout({ ...layoutBaseOpts, ...insets, overrides: mobileLayoutOverrides }),
      effectivePlayInsets: insets,
    };
  }, [layoutBaseOpts, isMobileViewport, playAreaInsets, layoutOverrides]);

  const layoutFootprintScale = seatLayout.footprintScale ?? 1;

  const renderSeatDims = useMemo(() => ({
    opponentW: seatDims.opponentW * layoutFootprintScale,
    myW: seatDims.myW * layoutFootprintScale,
    cardW: seatDims.cardW * layoutFootprintScale,
    cardH: seatDims.cardH * layoutFootprintScale,
    cardWMe: seatDims.cardWMe * layoutFootprintScale,
    cardHMe: seatDims.cardHMe * layoutFootprintScale,
    baseH: seatDims.baseH * layoutFootprintScale,
    fontMe: seatDims.fontMe * layoutFootprintScale,
    fontOpp: seatDims.fontOpp * layoutFootprintScale,
    fontSuitMe: seatDims.fontSuitMe * layoutFootprintScale,
    fontSuitOpp: seatDims.fontSuitOpp * layoutFootprintScale,
  }), [seatDims, layoutFootprintScale]);

  // ========== Dealer Reveal: Which latest card is at each seat? ==========
  const seatCount = allSeats.length;
  let lastCardForSeat = Array(seatCount).fill(null);
  if (dealerRevealDeck && typeof dealerRevealIdx === "number" && dealerRevealIdx >= 0) {
    for (let i = 0; i <= dealerRevealIdx; ++i) {
      const seatIdx = i % seatCount;
      lastCardForSeat[seatIdx] = dealerRevealDeck[i];
    }
  }



  useEffect(() => {
    // Only auto-scroll chat if user is already near the bottom
    if (chatListEnd.current) {
      const chatContainer = chatListEnd.current.parentElement;
      if (chatContainer) {
        const isNearBottom = chatContainer.scrollTop + chatContainer.clientHeight >= chatContainer.scrollHeight - 50;
        if (isNearBottom) {
          chatListEnd.current.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
  }, [chat]);

  // Reset dealerReadyClicked when leaving dealer_selected or once server confirms ready
  useEffect(() => {
    if (phase !== "dealer_selected") {
      setDealerReadyClicked(false);
    }
  }, [phase]);

  useEffect(() => {
    if (readies.includes(player?.playerID)) {
      setDealerReadyClicked(false);
    }
  }, [readies, player?.playerID]);

  // Dealer card highlight after selection (server drives reveal timing)
  useEffect(() => {
    if (!table || !player) return;
    if (!isDealerRevealPhase) return;
    if (dealerRevealDone && dealerRevealFound != null && !dealerHighlight) {
      const t = setTimeout(() => setDealerHighlight(true), 800);
      return () => clearTimeout(t);
    }
  }, [table, player, isDealerRevealPhase, dealerRevealDone, dealerRevealFound, dealerHighlight]);

  const [dealerDisplayClock, setDealerDisplayClock] = useState(Date.now());
  useEffect(() => {
    if (phase !== "dealer_selected") return;
    const timer = setInterval(() => setDealerDisplayClock(Date.now()), 250);
    return () => clearInterval(timer);
  }, [phase]);
  const canReadyToDeal = phase !== "dealer_selected" || dealerDisplayClock >= dealerRevealDisplayUntil;

  // Handle temporary message display for new chat messages
  useEffect(() => {
    if (chat && chat.length > 0) {
      const lastMessage = chat[chat.length - 1];
      if (!lastMessage.system && lastMessage.name !== player?.name) {
        setTempMessage(lastMessage);
        setTimeout(() => setTempMessage(null), 3000);
      }
    }
  }, [chat, player?.name]);

  // Auto-clear ante vote result after 3 seconds
  useEffect(() => {
    if (anteVoteComplete && anteVoteResult) {
      const timer = setTimeout(() => {
        // The server will clear the ante request state after a delay
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [anteVoteComplete, anteVoteResult]);

  const scrollHistoryToTop = () => {
    if (historyRef.current) historyRef.current.scrollTop = 0;
  };

  if (!table || !player) {
    return (
      <div style={{ 
        textAlign: "center", 
        marginTop: 70, 
        fontSize: 22,
        color: COLORS.text,
        fontFamily: "'Inter', system-ui, sans-serif"
      }}>
        Loading game...
      </div>
    );
  }

  // Enhanced styling functions
  function seatBackground(isWinner, isDealerFound) {
    if (isDealerFound) return "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)";
    if (isWinner) return "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)";
    return COLORS.surface;
  }

  function seatBorder(isWinner, isDealerFound) {
    if (isDealerFound) return `2px solid ${COLORS.success}`;
    if (isWinner) return `2px solid ${COLORS.warning}`;
    return `1px solid ${COLORS.border}`;
  }

  function displayAvatar(p) {
    return p.avatarUrl && p.avatarUrl.trim() !== ""
      ? p.avatarUrl
      : "/avatars/avatar1.png";
  }

  function shouldShowCards(idx) {
    if (isSpectate) return false;
    if (phase === "action") return idx === myIdx && idx === activePlayerIdx;
    if (phase === "reveal") {
      const knockIdx = revealOrder.indexOf(idx);
      return (
        knocked &&
        knocked.includes(idx) &&
        knockIdx > -1 &&
        knockIdx < revealedCount
      );
    }
    if (phase === "results" || phase === "done") return knocked && knocked.includes(idx);
    return false;
  }



  function getLosingKnockedHands(h) {
    if (h.losingKnockedHands && h.losingKnockedHands.length) {
      return h.losingKnockedHands;
    }
    const winners = Array.isArray(h.winnerIdx)
      ? h.winnerIdx
      : h.winnerIdx != null
        ? [h.winnerIdx]
        : [];
    return (h.knocked || [])
      .filter((idx) => !winners.includes(idx))
      .map((idx) => ({
        playerIdx: idx,
        playerName: allSeats[idx]?.name || "Unknown",
        hand: null,
        handType: null,
      }));
  }

  function getSeatCardDisplay(idx) {
    const hasHand = hands && hands[idx] && hands[idx].length > 0;
    if (isDealerRevealPhase && lastCardForSeat[idx]) {
      return { mode: "reveal", cards: [lastCardForSeat[idx]] };
    }
    if (!hasHand) return { mode: "none", cards: [] };
    const isMySeat = idx === myIdx;
    if (isMySeat && (phase === "action" || phase === "reveal" || phase === "results" || phase === "done")) {
      if (phase === "reveal" || phase === "results" || phase === "done") {
        if (shouldShowCards(idx) || (knocked && knocked.includes(idx))) {
          return { mode: "faceup", cards: hands[idx] };
        }
      } else {
        return { mode: "faceup", cards: hands[idx] };
      }
    }
    if (shouldShowCards(idx)) return { mode: "faceup", cards: hands[idx] };
    if (phase !== "waiting" && phase !== "suitvote") {
      return { mode: "back", cards: hands[idx] };
    }
    return { mode: "none", cards: [] };
  }

  function renderMiniCard(card, cardIdx, isMeSeat) {
    const w = isMeSeat ? renderSeatDims.cardWMe : renderSeatDims.cardW;
    const h = isMeSeat ? renderSeatDims.cardHMe : renderSeatDims.cardH;
    const red = card.suit === "hearts" || card.suit === "diamonds";
    return (
      <div
        key={`card-${cardIdx}-${card.rank}-${card.suit}`}
        style={{
          width: w,
          height: h,
          borderRadius: 4,
          background: "#fafafa",
          border: `1px solid ${CASINO.goldDim}`,
          boxShadow: "0 2px 4px rgba(0,0,0,0.35)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 800,
          fontSize: isMeSeat ? `${renderSeatDims.fontMe}rem` : `${renderSeatDims.fontOpp}rem`,
          lineHeight: 1,
          color: red ? "#dc2626" : "#111827",
        }}
      >
        <span>{card.rank}</span>
        <span style={{ fontSize: isMeSeat ? `${renderSeatDims.fontSuitMe}rem` : `${renderSeatDims.fontSuitOpp}rem` }}>{suitSymbols[card.suit]}</span>
      </div>
    );
  }

  function renderCardBacks(count, isMeSeat) {
    const w = isMeSeat ? renderSeatDims.cardWMe : renderSeatDims.cardW;
    const h = isMeSeat ? renderSeatDims.cardHMe : renderSeatDims.cardH;
    return Array.from({ length: count }, (_, i) => (
      <div
        key={`back-${i}`}
        style={{
          width: w,
          height: h,
          borderRadius: 4,
          background: CASINO.cardBack,
          border: `1px solid ${CASINO.goldDim}`,
          boxShadow: "0 2px 4px rgba(0,0,0,0.35)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.62rem",
          color: CASINO.gold,
          letterSpacing: 1,
        }}
      >
        GUTS
      </div>
    ));
  }

  function compactBadge(label, color, bg) {
    return (
      <span
        style={{
          fontSize: "0.62rem",
          fontWeight: 700,
          color,
          background: bg,
          padding: "1px 4px",
          borderRadius: 4,
          whiteSpace: "nowrap",
          lineHeight: 1.3,
        }}
      >
        {label}
      </span>
    );
  }

  function playerIndicators(idx, isBot) {
    if (discarded && discarded.includes(idx))
      return (
        <span style={{ 
          color: COLORS.danger, 
          fontWeight: "600",
          fontSize: "0.58rem",
          padding: "1px 4px",
          background: "#fef2f2",
          borderRadius: "12px",
          border: `1px solid ${COLORS.danger}20`
        }}>
          Discarded
        </span>
      );
    if (knocked && knocked.includes(idx))
      return (
        <span style={{ 
          color: COLORS.success, 
          fontWeight: "600",
          fontSize: "0.58rem",
          padding: "1px 4px",
          background: "#f0fdf4",
          borderRadius: "12px",
          border: `1px solid ${COLORS.success}20`
        }}>
          Knocked
        </span>
      );
    return (
      <span
        style={{
          fontSize: "0.58rem",
          color: isBot ? "#86efac" : "#93c5fd",
          fontWeight: "700",
          padding: "1px 4px",
          background: "rgba(255,255,255,0.08)",
          borderRadius: 4,
          border: `1px solid ${isBot ? "#166534" : "#1e40af"}`
        }}
      >
        {isBot ? "Bot" : "Human"}
      </span>
    );
  }

  // Event handlers
  function handleKnock() {
    if (!socket || isSpectate) return;
    socket.emit("playerAction", {
      tableID: table.id,
      playerID: player.playerID,
      action: "knock",
    });
  }

  function handleDiscard() {
    if (!socket || isSpectate) return;
    socket.emit("playerAction", {
      tableID: table.id,
      playerID: player.playerID,
      action: "discard",
    });
  }

  function handleSuitVote(suitKey) {
    if (!socket || isSpectate) return;
    socket.emit("suitVote", {
      tableID: table.id,
      playerID: player.playerID,
      suit: suitKey,
    });
  }

  function handleReadyForSuitVote() {
    if (!socket || isSpectate) return;
    socket.emit("readyForSuitVote", {
      tableID: table.id,
      playerID: player.playerID,
    });
  }

  function handleNextRoundReady() {
    if (!socket || isSpectate) return;
    socket.emit("nextRoundReady", {
      tableID: table.id,
      playerID: player.playerID,
    });
  }

  function handleAddBot() {
    if (!socket || isSpectate) return;
    socket.emit("addBot", { tableID: table.id, playerID: player.playerID });
  }

  function sendChat() {
    const trimmed = chatMsg.trim();
    if (!socket || !trimmed || isSpectate) return;
    socket.emit("chatMessage", {
      tableID: table.id,
      playerID: player.playerID,
      msg: trimmed,
    });
    setChatMsg("");
  }

  function handleRemoveSpectator(spectatorID) {
    if (!socket || !isHost) return;
    socket.emit("removeSpectator", {
      tableID: table.id,
      requesterID: player.playerID,
      targetSpectatorID: spectatorID,
    });
  }

  function handleTakeBreak() {
    if (socket && table && player) {
      socket.emit("leaveTable", { tableID: table.id, playerID: player.playerID });
    }
    if (typeof onTakeBreak === "function") onTakeBreak();
  }

  function handleQuitPermanent() {
    if (socket && table && player) {
      socket.emit("quitTable", { tableID: table.id, playerID: player.playerID });
    }
    if (typeof onQuit === "function") onQuit();
  }

  // Ante Request and Voting Handlers
  function handleRequestAnteChange(requestedAmount = 2) {
    if (socket && table && player) {
      socket.emit("requestAnteIncrease", {
        tableID: table.id,
        playerID: player.playerID,
        requestedAmount: requestedAmount
      });
    }
  }

  function handleAnteVote(vote) {
    if (socket && table && player) {
      socket.emit("anteVote", {
        tableID: table.id,
        playerID: player.playerID,
        vote: vote // "accept" or "reject"
      });
    }
  }

  function handleCancelAnteRequest() {
    if (socket && table && player) {
      socket.emit("cancelAnteRequest", {
        tableID: table.id,
        playerID: player.playerID
      });
    }
  }

  function handleCreditIncreaseVote(vote) {
    console.log(`DEBUG - Client: handleCreditIncreaseVote called with vote=${vote}`);
    if (socket && table && player) {
      console.log(`DEBUG - Client: Emitting creditIncreaseVote with tableID=${table.id}, playerID=${player.playerID}, vote=${vote}`);
      socket.emit("creditIncreaseVote", {
        tableID: table.id,
        playerID: player.playerID,
        vote: vote // "accept" or "reject"
      });
    } else {
      console.log(`DEBUG - Client: Cannot emit vote - socket=${!!socket}, table=${!!table}, player=${!!player}`);
    }
  }

  function handleGeneratePaymentReport() {
    const reportUrl = `${window.location.origin}/payment-report/?table=${encodeURIComponent(table.id || "")}`;
    const newWindow = window.open(reportUrl, '_blank', 'width=800,height=600');
    
    if (newWindow) {
      newWindow.onload = () => {
        // The new window will load the payment report page
      };
    }
  }

  const inviteLink = window.location.origin + "/?invite=1&table=" + (table.id || "");
  const tableCode = table.id || "";
  const readyList = readyPlayers || [];
  const resultReadies = readies || [];
  const isFirstHand = (round === 1 || !historyArr || historyArr.length === 0);

  const mySeatIdx = activeSeats.findIndex((s) => s.playerID === player?.playerID);
  const playerNeedsAction = !isSpectate && !localPendingPlayer && (
    (phase === "action" && mySeatIdx === activePlayerIdx) ||
    (phase === "dealer_selected" && dealerRevealFound != null && dealerRevealDone && canReadyToDeal && !(readies && readies.includes(player.playerID))) ||
    (phase === "waiting" && isFirstHand && !readyList.includes(player.playerID)) ||
    (phase === "results" && !resultReadies.includes(player.playerID))
  );

  const tabButtonFlex = isMobileViewport ? 1 : "0 0 auto";

  const dealerRevealBar = isDealerRevealPhase ? (
    <div className={`guts-dealer-reveal-bar ${isMobileViewport ? "guts-dealer-reveal-bar--mobile" : "guts-dealer-reveal-bar--desktop"}`}>
      <div className="guts-dealer-reveal-bar__title">
        Finding Dealer{dealerSuit ? ` [${suitSymbols[dealerSuit]}]` : ""}...
      </div>
      {dealerRevealFound == null && !dealerRevealDone && (
        <div className="guts-dealer-reveal-bar__detail">
          Revealing cards...
        </div>
      )}
      {dealerRevealFound != null && dealerRevealDone && (
        <>
          <div className="guts-dealer-reveal-bar__dealer">
            Dealer: <span className="guts-dealer-reveal-bar__dealer-name">{allSeats[dealerRevealFound]?.name}</span>
          </div>
          {phase === "dealer_reveal" && (
            <div className="guts-dealer-reveal-bar__detail">Review the winning card</div>
          )}
          {phase === "dealer_selected" && !canReadyToDeal && (
            <div className="guts-dealer-reveal-bar__detail">Showing winning card...</div>
          )}
          {phase === "dealer_selected" && canReadyToDeal && (
            <div className="guts-dealer-reveal-bar__detail">Click Ready to Deal</div>
          )}
        </>
      )}
    </div>
  ) : null;

  const suitVotePanel = phase === "suitvote" ? (
    <div
      className={`guts-suit-vote-panel ${isMobileViewport ? "guts-suit-vote-panel--mobile" : "guts-suit-vote-panel--desktop"}`}
      style={{
        textAlign: "center",
        padding: "16px",
        background: COLORS.surface,
        borderRadius: "12px",
        boxShadow: SHADOWS.md,
        border: `1px solid ${COLORS.border}`,
        maxWidth: 320,
        boxSizing: "border-box",
        ...(isMobileViewport
          ? { margin: "16px auto 24px auto", position: "relative", zIndex: 10 }
          : {}),
      }}
    >
      <div style={{
        fontWeight: "700",
        fontSize: "1rem",
        marginBottom: "12px",
        color: COLORS.text,
      }}>
        Vote for Dealer Suit
      </div>
      <div style={{
        display: "flex",
        justifyContent: "center",
        gap: "8px",
        marginBottom: "12px",
      }}>
        {SUIT_KEYS.map((suitKey) => {
          const chosen = (suitVotes || []).find((v) => v.playerID === player.playerID && v.suit === suitKey);
          const isDisabled = (suitVotes || []).some((v) => v.playerID === player.playerID);
          return (
            <button
              key={suitKey}
              disabled={isDisabled}
              onClick={() => handleSuitVote(suitKey)}
              style={{
                fontSize: "1.5rem",
                fontWeight: "700",
                background: chosen ? "#eff6ff" : COLORS.surface,
                color: suitKey === "hearts" || suitKey === "diamonds" ? "#dc2626" : COLORS.text,
                border: chosen ? `2px solid ${COLORS.primary}` : `2px solid ${COLORS.border}`,
                borderRadius: "8px",
                padding: "12px",
                cursor: isDisabled ? "not-allowed" : "pointer",
                transition: ANIMATIONS.hover,
                minWidth: "50px",
                minHeight: "50px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: chosen ? SHADOWS.md : SHADOWS.sm,
              }}
              onMouseEnter={(e) => {
                if (!isDisabled) {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow = SHADOWS.lg;
                }
              }}
              onMouseLeave={(e) => {
                if (!isDisabled) {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = chosen ? SHADOWS.md : SHADOWS.sm;
                }
              }}
            >
              {suitSymbols[suitKey]}
            </button>
          );
        })}
      </div>
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: "6px",
      }}>
        {allSeats.map((p) => (
          <div key={p.playerID} style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            padding: "4px 8px",
            background: (suitVotes || []).some((v) => v.playerID === p.playerID) ? "#f0fdf4" : "#f8fafc",
            borderRadius: "12px",
            border: `1px solid ${(suitVotes || []).some((v) => v.playerID === p.playerID) ? COLORS.success : COLORS.border}`,
          }}>
            <span style={{
              color: (suitVotes || []).some((v) => v.playerID === p.playerID) ? COLORS.success : COLORS.textMuted,
              fontWeight: "600",
              fontSize: "0.75rem",
            }}>
              {(suitVotes || []).some((v) => v.playerID === p.playerID) ? "✓" : "○"}
            </span>
            <span style={{
              fontSize: "0.75rem",
              color: COLORS.text,
              fontWeight: "500",
            }}>
              {p.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <div className="guts-table-root" style={{
      height: "100vh",
      display: "flex",
      flexDirection: "column",
      background: COLORS.background,
      fontFamily: "'Inter', system-ui, sans-serif",
      color: COLORS.text,
      padding: `${UI.pagePad}px`,
      maxWidth: showChat ? "calc(100vw - 300px)" : "100vw",
      margin: "0 auto",
      position: "relative",
      overflow: "hidden",
      boxSizing: "border-box"
    }}>
      {/* Temporary Message Display */}
      {tempMessage && (
        <div style={{
          position: "fixed",
          top: "20px",
          right: showChat ? "340px" : "20px",
          background: COLORS.primary,
          color: "white",
          padding: "12px 20px",
          borderRadius: "8px",
          boxShadow: SHADOWS.lg,
          zIndex: 1001,
          maxWidth: "300px",
          fontSize: "0.875rem",
          fontWeight: "600"
        }}>
          <strong>{tempMessage.name || "Player"}:</strong> {tempMessage.text}
        </div>
      )}
      {/* Notification Banner */}
      {leavingNotification && (
        <div style={{
          position: "fixed",
          left: "50%",
          top: "20px",
          transform: "translateX(-50%)",
          maxWidth: 450,
          background: COLORS.surface,
          color: COLORS.text,
          fontWeight: "600",
          fontSize: "1rem",
          border: `1px solid ${COLORS.border}`,
          borderRadius: "12px",
          textAlign: "center",
          padding: "16px 24px",
          zIndex: 200,
          boxShadow: SHADOWS.lg,
          display: "flex",
          alignItems: "center",
          gap: "12px"
        }}>
          <img 
            src={leavingNotification.avatar || "/avatars/avatar1.png"}
            style={{ 
              width: 40, 
              height: 40, 
              borderRadius: "50%", 
              border: `2px solid ${COLORS.border}` 
            }} 
            alt="avatar" 
          />
          {leavingNotification.msg}
        </div>
      )}

      {localPendingPlayer && (
        <div
          style={{
            position: "fixed",
            left: "50%",
            bottom: 24,
            transform: "translateX(-50%)",
            background: "#1e3a5f",
            color: "#fff",
            padding: "12px 18px",
            borderRadius: 12,
            zIndex: 950,
            boxShadow: SHADOWS.lg,
            fontWeight: 700,
            textAlign: "center",
            maxWidth: "90vw",
          }}
          role="status"
        >
          {localPendingPlayer.waitForNormalRound || localPendingPlayer.sitsOutReason === "special_win_hold"
            ? "Waiting for next normal round"
            : "Joins next hand"}
          <div style={{ fontWeight: 500, fontSize: "0.85rem", marginTop: 4, opacity: 0.9 }}>
            You are seated — not in this hand.
          </div>
        </div>
      )}

      {/* Ante Collection Notice */}
      {phase === "deal" && !anteCollected && (
        <div style={{
          position: "fixed",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          maxWidth: 400,
          background: COLORS.warning,
          color: "white",
          fontWeight: "700",
          fontSize: "1.25rem",
          border: `2px solid ${COLORS.warning}`,
          borderRadius: "16px",
          textAlign: "center",
          padding: "24px 32px",
          zIndex: 1000,
          boxShadow: SHADOWS.xl,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px"
        }}>
          <div style={{
            fontSize: "2rem",
            marginBottom: "8px"
          }}>
            💰
          </div>
          <div style={{ fontWeight: "800", fontSize: "1.5rem", marginBottom: "8px" }}>
            Collecting Antes
          </div>
          <div style={{ fontSize: "1.125rem", lineHeight: "1.4" }}>
            Each player contributes 1 chip to the pot
          </div>
        </div>
      )}

      {/* Dealer Change Notice */}
      {table.state?.showDealerChangeNotice && table.state?.willDealerChange && table.state?.dealerToBeIdx !== null && (
        <div style={{
          position: "fixed",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          maxWidth: 500,
          background: COLORS.primary,
          color: "white",
          fontWeight: "700",
          fontSize: "1.25rem",
          border: `2px solid ${COLORS.primary}`,
          borderRadius: "16px",
          textAlign: "center",
          padding: "32px 40px",
          zIndex: 1000,
          boxShadow: SHADOWS.xl,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px"
        }}>
          <div style={{
            fontSize: "2rem",
            marginBottom: "8px"
          }}>
            🎉
          </div>
          <div style={{ fontWeight: "800", fontSize: "1.5rem", marginBottom: "8px" }}>
            Special Win!
          </div>
          <div style={{ fontSize: "1.125rem", lineHeight: "1.4" }}>
            {(() => {
              const allSeats = [...(table.players || []), ...(table.bots || [])];
              const winner = allSeats[table.state.dealerToBeIdx];
              const earnings = table.state.winnerEarnings || 0;
              const earningsDollar = (earnings * (table.state.creditValue || 1.00)).toFixed(2);
              return winner ? `${winner.name} wins with a special hand!` : "Player wins with a special hand!";
            })()}
          </div>
          <div style={{ 
            fontSize: "1rem", 
            opacity: 0.9, 
            marginTop: "8px",
            fontWeight: "600",
            color: "#fbbf24"
          }}>
            {(() => {
              const earnings = table.state.winnerEarnings || 0;
              const earningsDollar = (earnings * (table.state.creditValue || 1.00)).toFixed(2);
              return `Earned ${earnings} chips ($${earningsDollar}) from the pot!`;
            })()}
          </div>
          <div style={{ 
            fontSize: "1rem", 
            opacity: 0.9, 
            marginTop: "8px",
            fontWeight: "600"
          }}>
            Deck will be shuffled and dealer will change!
          </div>
        </div>
      )}

      {/* Dealer No Face Card Notice */}
      {table.state?.showDealerNoFaceCardNotice && (
        <div style={{
          position: "fixed",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          maxWidth: 500,
          background: COLORS.warning,
          color: "white",
          fontWeight: "700",
          fontSize: "1.25rem",
          border: `2px solid ${COLORS.warning}`,
          borderRadius: "16px",
          textAlign: "center",
          padding: "32px 40px",
          zIndex: 1000,
          boxShadow: SHADOWS.xl,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px"
        }}>
          <div style={{
            fontSize: "2rem",
            marginBottom: "8px"
          }}>
            ⚠️
          </div>
          <div style={{ fontWeight: "800", fontSize: "1.5rem", marginBottom: "8px" }}>
            No Winner This Hand
          </div>
          <div style={{ fontSize: "1.125rem", lineHeight: "1.4" }}>
            Dealer doesn't have a face card.
          </div>
          <div style={{ 
            fontSize: "1rem", 
            opacity: 0.9, 
            marginTop: "8px",
            fontWeight: "600"
          }}>
            All players must ante next round.
          </div>
        </div>
      )}

      {/* All Discard Notice */}
      {table.state?.allDiscardNotice && (
        <div style={{
          position: "fixed",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          maxWidth: 500,
          background: COLORS.warning,
          color: "white",
          fontWeight: "700",
          fontSize: "1.25rem",
          border: `2px solid ${COLORS.warning}`,
          borderRadius: "16px",
          textAlign: "center",
          padding: "32px 40px",
          zIndex: 1000,
          boxShadow: SHADOWS.xl,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px"
        }}>
          <div style={{
            fontSize: "2rem",
            marginBottom: "8px"
          }}>
            🃏
          </div>
          <div style={{ fontWeight: "800", fontSize: "1.5rem", marginBottom: "8px" }}>
            No Winner This Hand
          </div>
          <div style={{ fontSize: "1.125rem", lineHeight: "1.4" }}>
            All players discarded.
          </div>
          <div style={{ 
            fontSize: "1rem", 
            opacity: 0.9, 
            marginTop: "8px",
            fontWeight: "600"
          }}>
            All players must ante again for the next round.
          </div>
        </div>
      )}

      {/* Ante Request Voting Interface */}
      {anteRequest && (!anteVoteComplete || state.autoAcceptCountdown !== undefined) && (
        <div style={{
          position: "fixed",
          left: "50%",
          top: "60%",
          transform: "translate(-50%, -50%)",
          maxWidth: 500,
          background: COLORS.surface,
          color: COLORS.text,
          fontWeight: "700",
          fontSize: "1.25rem",
          border: `2px solid ${COLORS.border}`,
          borderRadius: "16px",
          textAlign: "center",
          padding: "32px 40px",
          zIndex: 1000,
          boxShadow: SHADOWS.xl,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px"
        }}>
          <div style={{
            fontSize: "2rem",
            marginBottom: "8px"
          }}>
            🗳️
          </div>
          <div style={{ fontWeight: "800", fontSize: "1.5rem", marginBottom: "8px" }}>
            Ante Increase Request
          </div>
          <div style={{ fontSize: "1.125rem", lineHeight: "1.4", marginBottom: "16px" }}>
            {(() => {
              const allSeats = [...(table.players || []), ...(table.bots || [])];
              const requester = allSeats.find(p => p.playerID === anteRequest.requesterID);
              const baseMessage = requester ? `${requester.name} requests to change ante to ${anteRequest.requestedAmount} chips ($${(anteRequest.requestedAmount * creditValue).toFixed(2)})` : "A player requests to change the ante";
              
              // Add auto-accept countdown message if applicable
              if (state.autoAcceptCountdown !== undefined && state.autoAcceptCountdown > 0) {
                return `${baseMessage}\n\nCredit change auto-accepted in ${state.autoAcceptCountdown} seconds...`;
              }
              
              return baseMessage;
            })()}
          </div>
          
          {/* Show voting buttons only if this player can vote and it's not an auto-accept scenario */}
          {anteVotes.hasOwnProperty(player?.playerID) && anteVotes[player?.playerID] === null && state.autoAcceptCountdown === undefined && (
            <div style={{ display: "flex", gap: "16px", marginTop: "16px" }}>
              <button
                onClick={() => handleAnteVote("accept")}
                style={{
                  padding: "12px 24px",
                  background: COLORS.success,
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontWeight: "700",
                  fontSize: "1rem",
                  cursor: "pointer",
                  transition: ANIMATIONS.hover,
                  boxShadow: SHADOWS.sm
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#059669";
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = SHADOWS.md;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = COLORS.success;
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = SHADOWS.sm;
                }}
              >
                Accept
              </button>
              <button
                onClick={() => handleAnteVote("reject")}
                style={{
                  padding: "12px 24px",
                  background: COLORS.danger,
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontWeight: "700",
                  fontSize: "1rem",
                  cursor: "pointer",
                  transition: ANIMATIONS.hover,
                  boxShadow: SHADOWS.sm
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#dc2626";
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = SHADOWS.md;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = COLORS.danger;
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = SHADOWS.sm;
                }}
              >
                Reject
              </button>
            </div>
          )}
          
          {/* Show current vote status */}
          {anteVotes.hasOwnProperty(player?.playerID) && anteVotes[player?.playerID] !== null && (
            <div style={{ 
              fontSize: "1rem", 
              opacity: 0.9, 
              marginTop: "8px",
              fontWeight: "600",
              color: anteVotes[player?.playerID] === "accept" ? COLORS.success : COLORS.danger
            }}>
              You voted: {anteVotes[player?.playerID] === "accept" ? "Accept" : "Reject"}
            </div>
          )}
          
          {/* Show cancel button for requester */}
          {anteRequest.requesterID === player?.playerID && state.autoAcceptCountdown === undefined && (
            <button
              onClick={handleCancelAnteRequest}
              style={{
                padding: "8px 16px",
                background: COLORS.textMuted,
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontWeight: "600",
                fontSize: "0.875rem",
                cursor: "pointer",
                transition: ANIMATIONS.hover,
                marginTop: "16px"
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "#6b7280";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = COLORS.textMuted;
              }}
            >
              Cancel Request
            </button>
          )}
        </div>
      )}

      {/* Ante Vote Result Notice */}
      {anteVoteComplete && anteVoteResult && (
        <div style={{
          position: "fixed",
          left: "50%",
          top: "40%",
          transform: "translate(-50%, -50%)",
          maxWidth: 400,
          background: anteVoteResult === "accepted" ? COLORS.success : COLORS.danger,
          color: "white",
          fontWeight: "700",
          fontSize: "1.25rem",
          border: `2px solid ${anteVoteResult === "accepted" ? COLORS.success : COLORS.danger}`,
          borderRadius: "16px",
          textAlign: "center",
          padding: "24px 32px",
          zIndex: 1001,
          boxShadow: SHADOWS.xl,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px"
        }}>
          <div style={{
            fontSize: "2rem",
            marginBottom: "8px"
          }}>
            {anteVoteResult === "accepted" ? "✅" : "❌"}
          </div>
          <div style={{ fontWeight: "800", fontSize: "1.5rem", marginBottom: "8px" }}>
            {anteVoteResult === "accepted" ? "Ante Change Accepted!" : "Ante Change Rejected"}
          </div>
          <div style={{ fontSize: "1.125rem", lineHeight: "1.4" }}>
            {anteVoteResult === "accepted" 
              ? `Next round will have ${anteRequest?.requestedAmount || 2} chip ante ($${((anteRequest?.requestedAmount || 2) * creditValue).toFixed(2)})`
              : "Ante will remain at 1 chip for next round"
            }
          </div>
        </div>
      )}

      {/* Credit Increase Request Voting Interface */}
      {creditIncreaseRequest && !creditIncreaseVoteComplete && (
        <div style={{
          position: "fixed",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          maxWidth: 500,
          background: COLORS.surface,
          color: COLORS.text,
          fontWeight: "700",
          fontSize: "1.25rem",
          border: `2px solid ${COLORS.border}`,
          borderRadius: "16px",
          textAlign: "center",
          padding: "32px 40px",
          zIndex: 1002,
          boxShadow: SHADOWS.xl,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px"
        }}>
          <div style={{
            fontSize: "2rem",
            marginBottom: "8px"
          }}>
            💰
          </div>
          <div style={{ fontWeight: "800", fontSize: "1.5rem", marginBottom: "8px" }}>
            Credit Increase Request
          </div>
          <div style={{ fontSize: "1.125rem", lineHeight: "1.4", marginBottom: "16px" }}>
            {(() => {
              const increaseAmount = creditIncreaseRequest.requestedAmount;
              const affectedPlayers = creditIncreaseRequest.affectedPlayers || [];
              const affectedPlayerNames = affectedPlayers.map(playerID => {
                const player = allSeats.find(p => p.playerID === playerID);
                return player ? player.name : playerID;
              }).join(", ");
              
              return `Some players have insufficient credits to cover both the ante and potential knock loss.\n\nIncrease all players' credits by ${increaseAmount} chips ($${(increaseAmount * creditValue).toFixed(2)})?\n\nAffected players: ${affectedPlayerNames}`;
            })()}
          </div>
          
          {/* Show voting buttons only if this player can vote */}
          {creditIncreaseVotes.hasOwnProperty(player?.playerID) && creditIncreaseVotes[player?.playerID] === null && (
            <div style={{ display: "flex", gap: "16px", marginTop: "16px" }}>
              <button
                onClick={() => handleCreditIncreaseVote("accept")}
                style={{
                  padding: "12px 24px",
                  background: COLORS.success,
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontWeight: "700",
                  fontSize: "1rem",
                  cursor: "pointer",
                  transition: ANIMATIONS.hover,
                  boxShadow: SHADOWS.sm
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#059669";
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = SHADOWS.md;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = COLORS.success;
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = SHADOWS.sm;
                }}
              >
                Accept
              </button>
                             <button
                 onClick={() => handleCreditIncreaseVote("reject")}
                 style={{
                   padding: "12px 24px",
                   background: COLORS.danger,
                   color: "white",
                   border: "none",
                   borderRadius: "8px",
                   fontWeight: "700",
                   fontSize: "1rem",
                   cursor: "pointer",
                   transition: ANIMATIONS.hover,
                   boxShadow: SHADOWS.sm
                 }}
                 onMouseEnter={(e) => {
                   e.target.style.background = "#dc2626";
                   e.target.style.transform = "translateY(-1px)";
                   e.target.style.boxShadow = SHADOWS.md;
                 }}
                 onMouseLeave={(e) => {
                   e.target.style.background = COLORS.danger;
                   e.target.style.transform = "translateY(0)";
                   e.target.style.boxShadow = SHADOWS.sm;
                 }}
               >
                 Reject Request and Remove User
               </button>
            </div>
          )}
          
          {/* Show current vote status */}
          {creditIncreaseVotes.hasOwnProperty(player?.playerID) && creditIncreaseVotes[player?.playerID] !== null && (
            <div style={{ 
              fontSize: "1rem", 
              opacity: 0.9, 
              marginTop: "8px",
              fontWeight: "600",
              color: creditIncreaseVotes[player?.playerID] === "accept" ? COLORS.success : COLORS.danger
            }}>
              You voted: {creditIncreaseVotes[player?.playerID] === "accept" ? "Accept" : "Reject"}
            </div>
          )}
        </div>
      )}

      {/* Credit Increase Vote Result Notice */}
      {creditIncreaseVoteComplete && creditIncreaseVoteResult && (
        <div style={{
          position: "fixed",
          left: "50%",
          top: "30%",
          transform: "translate(-50%, -50%)",
          maxWidth: 400,
          background: creditIncreaseVoteResult === "accepted" ? COLORS.success : COLORS.danger,
          color: "white",
          fontWeight: "700",
          fontSize: "1.25rem",
          border: `2px solid ${creditIncreaseVoteResult === "accepted" ? COLORS.success : COLORS.danger}`,
          borderRadius: "16px",
          textAlign: "center",
          padding: "24px 32px",
          zIndex: 1003,
          boxShadow: SHADOWS.xl,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px"
        }}>
          <div style={{
            fontSize: "2rem",
            marginBottom: "8px"
          }}>
            {creditIncreaseVoteResult === "accepted" ? "✅" : "❌"}
          </div>
          <div style={{ fontWeight: "800", fontSize: "1.5rem", marginBottom: "8px" }}>
            {creditIncreaseVoteResult === "accepted" ? "Credit Increase Accepted!" : "Credit Increase Rejected"}
          </div>
                     <div style={{ fontSize: "1.125rem", lineHeight: "1.4" }}>
             {creditIncreaseVoteResult === "accepted" 
               ? `All players' credits increased by ${creditIncreaseRequest?.requestedAmount || 0} chips ($${((creditIncreaseRequest?.requestedAmount || 0) * creditValue).toFixed(2)})`
               : "Players without sufficient credits have been removed from the game"
             }
           </div>
        </div>
      )}

      {/* Header Section - compact viewport toolbar */}
      <div
        ref={headerRef}
        style={{
          background: COLORS.surface,
          borderRadius: UI.radius,
          padding: UI.headerPad,
          marginBottom: 6,
          boxShadow: SHADOWS.sm,
          border: `1px solid ${COLORS.border}`,
          flexShrink: 0,
        }}
      >
        <div className="guts-header-toolbar">
          <div className="guts-header-meta">
            <h1 style={{ margin: 0, fontSize: UI.title, fontWeight: 700, color: COLORS.text, lineHeight: 1.1 }}>
              Guts
            </h1>
            <code style={{
              fontFamily: "monospace",
              fontSize: UI.meta,
              color: COLORS.primary,
              background: "#eff6ff",
              padding: UI.chipPad,
              borderRadius: 6,
              border: `1px solid ${COLORS.primary}20`,
            }}>
              {(tableCode || "").toUpperCase()}
            </code>
            <span className="guts-chip" style={{
              background: COLORS.success,
              color: "white",
              padding: UI.chipPad,
              borderRadius: 6,
              fontWeight: 600,
              fontSize: UI.meta,
            }}>
              Pot {pot} (${(pot * creditValue).toFixed(2)})
            </span>
            {player && !player.isSpectator && (
              <span className="guts-chip" style={{
                background: COLORS.primary,
                color: "white",
                padding: UI.chipPad,
                borderRadius: 6,
                fontWeight: 600,
                fontSize: UI.meta,
              }}>
                Credits {credits[player.playerID] || 0} (${((credits[player.playerID] || 0) * creditValue).toFixed(2)})
              </span>
            )}
            {anteCollected && (
              <span className="guts-chip" style={{
                background: COLORS.warning,
                color: "white",
                padding: UI.chipPad,
                borderRadius: 6,
                fontWeight: 600,
                fontSize: UI.meta,
              }}>
                Ante in
              </span>
            )}
            {(dealerSuit && typeof dealerIdx === "number" && allSeats[dealerIdx]) && (
              <span className="guts-chip" style={{ fontSize: UI.meta, color: COLORS.textLight }}>
                Dealer <span style={{ color: suitColors[dealerSuit], fontWeight: 700 }}>{suitSymbols[dealerSuit]}</span> {allSeats[dealerIdx].name}
              </span>
            )}
            {phase !== "waiting" && (
              <span className="guts-chip" style={{
                padding: UI.chipPad,
                background: "#eff6ff",
                color: COLORS.primary,
                borderRadius: 999,
                fontSize: UI.meta,
                fontWeight: 600,
                border: `1px solid ${COLORS.primary}20`,
              }}>
                R{round} - {PHASE_LABELS[phase] || phase}
              </span>
            )}
            {spectators.length > 0 && (
              <span className="guts-chip" style={{ fontSize: UI.meta, color: COLORS.textLight }}>
                Watching: {spectators.length}
              </span>
            )}
            {isSpectate && (
              <span className="guts-chip" style={{
                padding: UI.chipPad,
                background: "#fef3c7",
                color: "#92400e",
                borderRadius: 6,
                fontSize: UI.meta,
                fontWeight: 600,
                border: "1px solid #fbbf24",
              }}>
                Spectating
              </span>
            )}
          </div>

          <div className="guts-header-actions">
            <button
              className="guts-btn-compact"
              onClick={() => setShowInviteModal(true)}
              style={{
                padding: UI.btnPad,
                background: COLORS.primary,
                color: "white",
                border: "none",
                borderRadius: UI.radius,
                fontWeight: 600,
                fontSize: UI.btnFont,
                cursor: "pointer",
                boxShadow: SHADOWS.sm,
              }}
            >
              Invite
            </button>
            {isHost && !isSpectate && (
              <button
                className="guts-btn-compact"
                onClick={handleAddBot}
                disabled={allSeats.length >= 7 || (phase && phase !== "waiting" && phase !== "results")}
                style={{
                  padding: UI.btnPad,
                  background: (allSeats.length >= 7 || (phase && phase !== "waiting" && phase !== "results")) ? COLORS.textMuted : COLORS.success,
                  color: "white",
                  border: "none",
                  borderRadius: UI.radius,
                  fontWeight: 600,
                  fontSize: UI.btnFont,
                  cursor: (allSeats.length >= 7 || (phase && phase !== "waiting" && phase !== "results")) ? "not-allowed" : "pointer",
                }}
              >
                + Bot
              </button>
            )}
            {!isSpectate && (phase === "waiting" || phase === "results") && !anteRequest && (
              <>
                <select
                  value={selectedAnteAmount}
                  onChange={(e) => setSelectedAnteAmount(parseInt(e.target.value))}
                  style={{
                    padding: "2px 4px",
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 6,
                    fontSize: UI.meta,
                    maxWidth: 88,
                  }}
                >
                  {Array.from({ length: 20 }, (_, i) => i + 1).map((num) => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
                <button
                  className="guts-btn-compact"
                  onClick={() => handleRequestAnteChange(selectedAnteAmount)}
                  style={{
                    padding: UI.btnPad,
                    background: COLORS.warning,
                    color: "white",
                    border: "none",
                    borderRadius: UI.radius,
                    fontWeight: 600,
                    fontSize: UI.btnFont,
                    cursor: "pointer",
                  }}
                >
                  Ante
                </button>
              </>
            )}
            <button
              className="guts-btn-compact"
              onClick={() => setShowChat(!showChat)}
              style={{
                padding: UI.btnPad,
                background: showChat ? COLORS.secondary : COLORS.primary,
                color: "white",
                border: "none",
                borderRadius: UI.radius,
                fontWeight: 600,
                fontSize: UI.btnFont,
                cursor: "pointer",
              }}
            >
              {showChat ? "Chat -" : "Chat +"}
            </button>
            <button
              className="guts-btn-compact"
              style={{
                padding: UI.btnPad,
                background: COLORS.success,
                color: "white",
                border: "none",
                borderRadius: UI.radius,
                fontWeight: 600,
                fontSize: UI.btnFont,
                cursor: "pointer",
              }}
              onClick={handleGeneratePaymentReport}
            >
              Report
            </button>
            <button
              className="guts-btn-compact"
              type="button"
              title="Rules and feature guide"
              style={{
                padding: UI.btnPad,
                background: COLORS.secondary,
                color: "white",
                border: "none",
                borderRadius: UI.radius,
                fontWeight: 600,
                fontSize: UI.btnFont,
                cursor: "pointer",
              }}
              onClick={openGameHelp}
            >
              Help
            </button>
            <button
              className="guts-btn-compact"
              style={{
                padding: UI.btnPad,
                background: COLORS.danger,
                color: "white",
                border: "none",
                borderRadius: UI.radius,
                fontWeight: 600,
                fontSize: UI.btnFont,
                cursor: "pointer",
              }}
              onClick={handleTakeBreak}
            >
              Take Break
            </button>
            <button
              type="button"
              onClick={handleQuitPermanent}
              style={{
                padding: UI.btnPad,
                background: "transparent",
                color: COLORS.textMuted,
                border: `1px solid ${COLORS.border}`,
                borderRadius: UI.radius,
                fontWeight: 500,
                fontSize: "0.75rem",
                cursor: "pointer",
              }}
            >
              Leave permanently
            </button>
          </div>
        </div>
      </div>

      <div className="guts-header-secondary">
      <div ref={phaseBannerRef} className="guts-phase-banner">
      {isMobileViewport && suitVotePanel}
      {isMobileViewport && dealerRevealBar}
      </div>

      {/* Tabs — desktop: far right below header */}
      <div ref={tabsRef} className="guts-header-nav">
        <div
          className="guts-header-nav__tabs"
          style={{
            display: "flex",
            gap: 0,
            background: COLORS.surface,
            borderRadius: 8,
            padding: "2px",
            boxShadow: SHADOWS.sm,
            border: `1px solid ${COLORS.border}`,
          }}
        >
        <button 
          onClick={() => { setHistoryTab(false); setPaymentTab(false); }} 
          style={{
            fontSize: "0.78rem", 
            fontWeight: '600', 
            padding: "5px 10px", 
            borderRadius: "8px",
            border: 'none',
            background: (!historyTab && !paymentTab) ? COLORS.primary : 'transparent',
            color: (!historyTab && !paymentTab) ? 'white' : COLORS.text,
            cursor: "pointer",
            transition: ANIMATIONS.hover,
            flex: tabButtonFlex
          }}
          onMouseEnter={(e) => {
            if (historyTab || paymentTab) {
              e.target.style.background = "#f1f5f9";
            }
          }}
          onMouseLeave={(e) => {
            if (historyTab || paymentTab) {
              e.target.style.background = 'transparent';
            }
          }}
        >
          Game Table
        </button>
        <button 
          onClick={() => { setHistoryTab(true); setPaymentTab(false); scrollHistoryToTop(); }} 
          style={{
            fontSize: "0.78rem", 
            fontWeight: '600', 
            padding: "5px 10px", 
            borderRadius: "8px",
            border: 'none',
            background: historyTab ? COLORS.primary : 'transparent',
            color: historyTab ? 'white' : COLORS.text,
            cursor: "pointer",
            transition: ANIMATIONS.hover,
            flex: tabButtonFlex
          }}
          onMouseEnter={(e) => {
            if (!historyTab) {
              e.target.style.background = "#f1f5f9";
            }
          }}
          onMouseLeave={(e) => {
            if (!historyTab) {
              e.target.style.background = 'transparent';
            }
          }}
        >
          Hand History
        </button>
        <button 
          onClick={() => { setHistoryTab(false); setPaymentTab(true); }} 
          style={{
            fontSize: "0.78rem", 
            fontWeight: '600', 
            padding: "5px 10px", 
            borderRadius: "8px",
            border: 'none',
            background: paymentTab ? COLORS.primary : 'transparent',
            color: paymentTab ? 'white' : COLORS.text,
            cursor: "pointer",
            transition: ANIMATIONS.hover,
            flex: tabButtonFlex
          }}
          onMouseEnter={(e) => {
            if (!paymentTab) {
              e.target.style.background = "#f1f5f9";
            }
          }}
          onMouseLeave={(e) => {
            if (!paymentTab) {
              e.target.style.background = 'transparent';
            }
          }}
        >
          Payment Tracking
        </button>
        </div>
      </div>
      </div>
      <div className="guts-tab-content" style={{ position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      {!isMobileViewport && !historyTab && !paymentTab && suitVotePanel}
      {!isMobileViewport && !historyTab && !paymentTab && dealerRevealBar}
      {historyTab && (
        <div 
          id="history-log-scroll" 
          ref={historyRef}
          style={{ 
            background: COLORS.surface, 
            borderRadius: "16px", 
            minHeight: 0,
            flex: 1,
            maxHeight: "none", 
            overflow: 'auto', 
            marginBottom: 24, 
            padding: 24, 
            border: `1px solid ${COLORS.border}`,
            fontSize: 14,
            boxShadow: SHADOWS.md
          }}
        >
          <div style={{ 
            fontWeight: "700", 
            fontSize: "1.25rem",
            marginBottom: "16px",
            color: COLORS.text
          }}>
            Hand History
          </div>
          <div style={{ marginTop: 16 }}>
            {(historyArr && historyArr.length)
              ? historyArr.slice().map((h, i) =>
                <div key={i} style={{ 
                  marginBottom: 20,
                  padding: "16px",
                  background: "#f8fafc",
                  borderRadius: "12px",
                  border: `1px solid ${COLORS.border}`
                }}>
                  <div style={{ 
                    fontWeight: '700', 
                    color: COLORS.primary,
                    fontSize: "1rem",
                    marginBottom: "8px"
                  }}>
                    Round {h.round}:
                  </div>
                  <div style={{ 
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px",
                    fontSize: "0.875rem"
                  }}>
                    <div><strong>Winner{Array.isArray(h.winnerIdx) && h.winnerIdx.length > 1 ? 's' : ''}:</strong> <span style={{ color: COLORS.success }}>{Array.isArray(h.winnerIdx) ? h.winnerIdx.map(idx => allSeats[idx]?.name).join(", ") : allSeats[h.winnerIdx]?.name || "unknown"}</span></div>
                    <div><strong>Dealer:</strong> {allSeats[h.dealerIdx]?.name}</div>
                    <div><strong>Winning Hand:</strong> <span style={{ color: COLORS.primary, fontWeight: "600" }}>{h.winningHand || "Unknown"}</span></div>
                    <div><strong>Hand Type:</strong> <span style={{ color: COLORS.textMuted }}>{h.winningHandType || "Unknown"}</span></div>
                    {h.pot != null && (
                      <div><strong>Winning Pot:</strong> <span style={{ color: COLORS.primary, fontWeight: "600" }}>{h.pot} chips (${(h.pot * creditValue).toFixed(2)})</span></div>
                    )}
                    <div><strong>Knocked:</strong> {h.knocked.map(idx => allSeats[idx]?.name).join(", ")}</div>
                    <div><strong>Discarded:</strong> {(h.discarded || []).map(idx => allSeats[idx]?.name).join(", ") || "(none)"}</div>
                    {getLosingKnockedHands(h).length > 0 && (
                      <div style={{ gridColumn: "1 / -1", marginTop: 4 }}>
                        <strong>Losing Knocked Hands:</strong>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
                          {getLosingKnockedHands(h).map((entry, li) => (
                            <div
                              key={`${entry.playerIdx}-${li}`}
                              style={{
                                padding: "6px 10px",
                                background: "#fef2f2",
                                borderRadius: 8,
                                border: `1px solid ${COLORS.danger}33`,
                                fontSize: "0.82rem",
                              }}
                            >
                              <span style={{ fontWeight: 700, color: COLORS.text }}>{entry.playerName}</span>
                              {entry.hand ? (
                                <>
                                  {": "}
                                  <span style={{ color: COLORS.danger, fontWeight: 700 }}>{entry.hand}</span>
                                  {entry.handType && (
                                    <span style={{ color: COLORS.textMuted }}> ({entry.handType})</span>
                                  )}
                                </>
                              ) : (
                                <span style={{ color: COLORS.textMuted }}> (hand not recorded)</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {h.dealerChanged && (
                      <div style={{ 
                        gridColumn: "1 / -1", 
                        color: COLORS.warning, 
                        fontWeight: "700",
                        fontSize: "0.9rem",
                        marginTop: "4px",
                        padding: "4px 8px",
                        background: "#fef3c7",
                        borderRadius: "6px",
                        border: "1px solid #f59e0b"
                      }}>
                        🎉 Special Win! Dealer changed to {allSeats[h.newDealerIdx]?.name || "unknown"}
                      </div>
                    )}
                  </div>
                </div>
              )
              : <div style={{ 
                  color: COLORS.textMuted,
                  textAlign: "center",
                  padding: "32px",
                  fontSize: "1rem"
                }}>
                  No previous hands yet.
                </div>
            }
          </div>
        </div>
      )}
      {paymentTab && (
        <div 
          style={{ 
            background: COLORS.surface, 
            borderRadius: "16px", 
            minHeight: 0,
            flex: 1,
            maxHeight: "none", 
            overflow: 'auto', 
            marginBottom: 24, 
            padding: 24, 
            border: `1px solid ${COLORS.border}`,
            fontSize: 14,
            boxShadow: SHADOWS.md
          }}
        >
          <div style={{ 
            fontWeight: "700", 
            fontSize: "1.25rem",
            marginBottom: "16px",
            color: COLORS.text
          }}>
            Payment Tracking
          </div>
          <div style={{ marginTop: 16 }}>
            {/* Credit Value Display */}
            <div style={{ 
              marginBottom: "16px",
              padding: "12px",
              background: "#f0f9ff",
              borderRadius: "8px",
              border: `1px solid ${COLORS.primary}`,
              textAlign: "center"
            }}>
              <div style={{ fontWeight: "600", color: COLORS.primary, marginBottom: "4px" }}>
                Credit Value: ${creditValue.toFixed(2)} per chip
              </div>
            </div>
            
            {/* Payment Tracking Summary */}
            <div style={{ marginBottom: "20px" }}>
              <div style={{ 
                fontWeight: "700", 
                fontSize: "1rem",
                marginBottom: "12px",
                color: COLORS.text
              }}>
                Your Net Payment Summary:
              </div>
              {calculatePaymentTracking && calculatePaymentTracking.length > 0 ? (
                calculatePaymentTracking.map((tracking, i) => (
                  <div key={i} style={{ 
                    marginBottom: "12px",
                    padding: "12px",
                    background: tracking.youOweThem > 0 ? "#fef2f2" : "#f0fdf4",
                    borderRadius: "8px",
                    border: `1px solid ${tracking.youOweThem > 0 ? "#fecaca" : "#bbf7d0"}`
                  }}>
                    <div style={{ 
                      fontWeight: "600", 
                      fontSize: "0.875rem",
                      marginBottom: "4px"
                    }}>
                      {tracking.playerName}
                    </div>
                    <div style={{ 
                      fontSize: "0.875rem",
                      color: COLORS.textMuted,
                      display: "flex",
                      flexDirection: "column",
                      gap: 4
                    }}>
                      {tracking.youOweThem > 0 && (
                        <strong style={{ color: COLORS.error }}>
                          You owe ${(tracking.youOweThem * creditValue).toFixed(2)}
                        </strong>
                      )}
                      {tracking.theyOweYou > 0 && (
                        <strong style={{ color: COLORS.success }}>
                          Owes you ${(tracking.theyOweYou * creditValue).toFixed(2)}
                        </strong>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ 
                  color: COLORS.textMuted,
                  textAlign: "center",
                  padding: "16px",
                  fontSize: "0.875rem"
                }}>
                  No payment activity yet.
                </div>
              )}
            </div>
            
            {/* Detailed Payment History */}
            <div style={{ 
              fontWeight: "700", 
              fontSize: "1rem",
              marginBottom: "12px",
              color: COLORS.text
            }}>
              Detailed History:
            </div>
            {(paymentHistory && paymentHistory.length)
              ? paymentHistory.slice().reverse().map((payment, i) =>
                <div key={i} style={{ 
                  marginBottom: 16,
                  padding: "12px",
                  background: "#f8fafc",
                  borderRadius: "8px",
                  border: `1px solid ${COLORS.border}`
                }}>
                  <div style={{ 
                    fontWeight: '700', 
                    color: COLORS.primary,
                    fontSize: "0.875rem",
                    marginBottom: "6px"
                  }}>
                    Round {payment.round}:
                  </div>
                  <div style={{ 
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "6px",
                    fontSize: "0.75rem"
                  }}>
                    <div><strong>Type:</strong> <span style={{ color: payment.type === 'win' ? COLORS.success : COLORS.warning, fontWeight: "600" }}>{payment.type === 'win' ? 'Single Winner' : 'Tie'}</span></div>
                    <div><strong>Pot:</strong> <span style={{ color: COLORS.primary, fontWeight: "600" }}>{payment.pot} chips (${(payment.pot * creditValue).toFixed(2)})</span></div>
                    {payment.type === 'win' ? (
                      <>
                        <div><strong>Winner:</strong> <span style={{ color: COLORS.success, fontWeight: "600" }}>{(allSeats || []).find(p => p.playerID === payment.winner)?.name || "Unknown"}</span></div>
                        <div><strong>Won:</strong> <span style={{ color: COLORS.success, fontWeight: "600" }}>{payment.amount} chips (${(payment.amount * creditValue).toFixed(2)})</span></div>
                      </>
                    ) : (
                      <>
                        <div><strong>Tied Players:</strong> <span style={{ color: COLORS.warning, fontWeight: "600" }}>{(payment.players || []).map(id => (allSeats || []).find(p => p.playerID === id)?.name || "Unknown").join(", ")}</span></div>
                        <div><strong>Split:</strong> <span style={{ color: COLORS.warning, fontWeight: "600" }}>{payment.amount} chips each (${(payment.amount * creditValue).toFixed(2)})</span></div>
                      </>
                    )}
                  </div>
                </div>
              )
              : <div style={{ 
                  color: COLORS.textMuted,
                  textAlign: "center",
                  padding: "16px",
                  fontSize: "0.875rem"
                }}>
                  No payment history yet.
                </div>
            }
          </div>
        </div>
      )}
        <div className="guts-table-viewport" style={{ flex: 1, minHeight: 0, display: historyTab || paymentTab ? "none" : undefined }}>
          <div
            className="guts-table-scale-wrap"
            style={{
              width: tableDesign.width,
              height: tableDesign.height,
              transform: `scale(${tableScale * layoutFitScale})`,
              transformOrigin: "center center",
            }}
          >
          {/* Poker Table Container */}
          <div style={{
            position: "relative",
            width: "100%",
            height: "100%",
            margin: 0,
            padding: `${effectivePlayInsets.paddingTop}px ${effectivePlayInsets.paddingRight}px ${effectivePlayInsets.paddingBottom}px ${effectivePlayInsets.paddingLeft}px`,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            boxSizing: "border-box",
            overflow: "visible"
          }}>
            {/* Central Table — centered on seat orbit, shifted for desktop chrome */}
            <div style={{
              position: "absolute",
              left: `calc(50% + ${seatLayout.playOffsetX || 0}px)`,
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: `${TABLE_FELT.width}px`,
              height: `${TABLE_FELT.height}px`,
              background: "radial-gradient(ellipse at center, #10b981 0%, #047857 55%, #065f46 100%)",
              borderRadius: "50%",
              border: `8px solid ${CASINO.goldDim}`,
              boxShadow: SHADOWS.xl,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1
            }}>
              {/* Table Center Content */}
              <div style={{
                textAlign: "center",
                color: "white",
                fontWeight: "700"
              }}>
                <div style={{
                  fontSize: "1.57rem",
                  marginBottom: "6px"
                }}>
                  GUTS
                </div>
                <div style={{
                  fontSize: "0.99rem",
                  opacity: 0.9
                }}>
                  {phase === "waiting" ? "Waiting for players..." : 
                   phase === "suitvote" ? "Vote for Dealer Suit" :
                   phase === "dealer_reveal" ? "Finding Dealer..." :
                   phase === "dealer_selected" ? "Dealer Selected" :
                   phase === "deal" ? "Dealing cards..." :
                   phase === "action" ? "Deciding knock/discard..." :
                   phase === "reveal" ? "Revealing knocked hands..." :
                   phase === "results" ? "Hand Complete!" : "Game in Progress"}
                </div>
                {phase !== "waiting" && (
                  <div style={{
                    fontSize: "0.875rem",
                    opacity: 0.8,
                    marginTop: "8px"
                  }}>
                    Round {round}
                  </div>
                )}
              </div>
            </div>

            {/* Player Seats - Poker Table Layout */}
            {(allSeats || []).map((p, idx) => {
              const isWin = (Array.isArray(winnerIdx) ? winnerIdx.includes(idx) : winnerIdx === idx) && (phase === "results" || phase === "done");
              const isBot = p.isBot;
              const isDealer = dealerIdx === idx;
              const isMe = p.playerID === player.playerID;
              const isMyTurn = phase === "action" && idx === activePlayerIdx;
              const isDealerFoundSeat = isDealerRevealPhase && dealerRevealFound === idx && dealerRevealDone;
              const seatW = isMe ? renderSeatDims.myW : renderSeatDims.opponentW;
              const cardInfo = getSeatCardDisplay(idx);
              const roundContributions = table.state?.roundContributions || {};
              const playerAnte = roundContributions[p.playerID] || 0;
              const readyLabel = phase === "waiting"
                ? (readyList.includes(p.playerID) ? "Ready" : "Wait")
                : phase === "results"
                  ? (resultReadies.includes(p.playerID) ? "Ready" : "Wait")
                  : null;

              const seatPos = seatLayout.positions[idx] || { x: 0, y: 0 };
              const playOffsetX = seatLayout.playOffsetX || 0;
              const seatX = seatPos.x;
              const seatY = seatPos.y;

              const borderColor = isWin
                ? CASINO.gold
                : isMe && (isMyTurn || isDealerFoundSeat)
                  ? CASINO.gold
                  : isDealer || isDealerFoundSeat
                    ? CASINO.goldDim
                    : "#374151";

              return (
                <div
                  key={p.playerID}
                  className={["seat-card", isWin ? "winner" : ""].filter(Boolean).join(" ")}
                  style={{
                    position: "absolute",
                    left: `calc(50% + ${playOffsetX + seatX}px)`,
                    top: `calc(50% + ${seatY}px)`,
                    transform: `translate(-50%, -50%) ${isMe ? "scale(1.03)" : "scale(1)"} ${isWin ? "scale(1.03)" : ""}`,
                    textAlign: "center",
                    width: seatW,
                    minWidth: seatW,
                    maxWidth: seatW,
                    border: `2px solid ${borderColor}`,
                    borderRadius: 8,
                    padding: isMe ? "8px 8px 7px" : "6px 6px 5px",
                    background: isWin ? CASINO.seatWin : isMe ? CASINO.seatMe : CASINO.seatOpp,
                    boxSizing: "border-box",
                    boxShadow: isMe && isMyTurn
                      ? `0 0 14px ${CASINO.gold}88, 0 4px 12px rgba(0,0,0,0.45)`
                      : "0 3px 10px rgba(0,0,0,0.4)",
                    transition: ANIMATIONS.transition,
                    zIndex: getSeatDepthZ(seatY, { isWinner: isWin }),
                    color: CASINO.text,
                  }}
                >
                  {isWin && (
                    <div style={{
                      position: "absolute",
                      top: -24,
                      left: "50%",
                      transform: "translateX(-50%)",
                      background: "linear-gradient(135deg, #fde68a, #f59e0b)",
                      color: "#78350f",
                      fontSize: "0.65rem",
                      fontWeight: 800,
                      padding: "2px 9px",
                      borderRadius: 999,
                      border: `1px solid ${CASINO.gold}`,
                      whiteSpace: "nowrap",
                      zIndex: 12,
                    }}>
                      WINNER +{table.state.winnerEarnings || 0}
                    </div>
                  )}

                  {isBot && isHost && (phase === "waiting" || phase === "results") && (
                    <button
                      onClick={() => socket.emit("removeBot", {
                        tableID: table.id,
                        playerID: player.playerID,
                        botPlayerID: p.playerID,
                      })}
                      title="Remove Bot"
                      style={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        border: "none",
                        background: COLORS.danger,
                        color: "white",
                        fontWeight: 700,
                        fontSize: "0.65rem",
                        cursor: "pointer",
                        borderRadius: "50%",
                        width: 18,
                        height: 18,
                        lineHeight: "14px",
                        padding: 0,
                        zIndex: 9,
                      }}
                    >
                      x
                    </button>
                  )}

                  {/* Header row: avatar + name + badges */}
                  <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 3 }}>
                    <div style={{ position: "relative", flexShrink: 0 }}>
                      <img
                        src={displayAvatar(p)}
                        alt={p.name}
                        width={isMe ? 36 : 29}
                        height={isMe ? 36 : 29}
                        style={{
                          borderRadius: "50%",
                          border: `1.5px solid ${isDealer || isDealerFoundSeat ? CASINO.gold : "#4b5563"}`,
                          objectFit: "cover",
                        }}
                      />
                      {(isDealer || isDealerFoundSeat) && (
                        <span style={{
                          position: "absolute",
                          bottom: -4,
                          right: -6,
                          fontSize: "0.45rem",
                          background: CASINO.gold,
                          color: "#111",
                          fontWeight: 800,
                          padding: "0 3px",
                          borderRadius: 3,
                          lineHeight: 1.2,
                        }}>D</span>
                      )}
                    </div>
                    <div style={{ minWidth: 0, flex: 1, textAlign: "left" }}>
                      <div style={{
                        fontWeight: 700,
                        fontSize: isMe ? "0.88rem" : "0.78rem",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: CASINO.text,
                      }}>
                        {p.name}{idx === 0 ? " 👑" : ""}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 2, marginTop: 1 }}>
                        {playerIndicators(idx, isBot)}
                        {freeHands[p.playerID] && compactBadge("FREE", "#86efac", "rgba(22,101,52,0.5)")}
                      </div>
                    </div>
                  </div>

                  {/* Cards */}
                  <div style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 4,
                    margin: "2px 0 3px",
                    minHeight: isMe ? renderSeatDims.cardHMe : renderSeatDims.cardH,
                  }}>
                    {cardInfo.mode === "reveal" && cardInfo.cards.map((c, ci) => renderMiniCard(c, ci, isMe))}
                    {cardInfo.mode === "faceup" && cardInfo.cards.map((c, ci) => renderMiniCard(c, ci, isMe))}
                    {cardInfo.mode === "back" && renderCardBacks(cardInfo.cards.length || 2, isMe)}
                  </div>

                  {/* Stats row - full info, compact */}
                  <div style={{
                    fontSize: "0.56rem",
                    color: CASINO.textMuted,
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "2px 4px",
                    lineHeight: 1.25,
                    marginBottom: isMe ? 3 : 0,
                  }}>
                    <span>Cr:{credits[p.playerID] || 0}</span>
                    {(playerAnte > 0 || (phase === "deal" && !anteCollected)) && (
                      <span>Ante:{playerAnte || 1}</span>
                    )}
                    {readyLabel && <span style={{ color: readyLabel === "Ready" ? "#86efac" : CASINO.textMuted }}>{readyLabel}</span>}
                  </div>

                  {/* Controls - my seat only */}
                  {isMe && !isSpectate && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 2 }}>
                      {phase === "action" && idx === activePlayerIdx && (
                        <div style={{ display: "flex", gap: 3 }}>
                          <button
                            onClick={handleKnock}
                            style={{
                              flex: 1,
                              background: "#166534",
                              border: `1px solid ${CASINO.goldDim}`,
                              padding: "4px 2px",
                              borderRadius: 5,
                              fontWeight: 700,
                              fontSize: "0.69rem",
                              color: "#ecfdf5",
                              cursor: "pointer",
                            }}
                          >
                            Knock
                          </button>
                          <button
                            onClick={handleDiscard}
                            style={{
                              flex: 1,
                              background: "#7f1d1d",
                              border: `1px solid ${CASINO.goldDim}`,
                              padding: "4px 2px",
                              borderRadius: 5,
                              fontWeight: 700,
                              fontSize: "0.69rem",
                              color: "#fef2f2",
                              cursor: "pointer",
                            }}
                          >
                            Fold
                          </button>
                        </div>
                      )}

                      {phase === "dealer_selected" && dealerRevealFound != null && dealerRevealDone && (
                        <button
                          onClick={() => {
                            setDealerReadyClicked(true);
                            socket.emit("dealerSelectedReady", {
                              tableID: table.id,
                              playerID: player.playerID,
                            });
                          }}
                          disabled={!canReadyToDeal || (readies && readies.includes(player.playerID))}
                          style={{
                            background: (dealerReadyClicked || (readies && readies.includes(player.playerID))) ? "#166534" : "#854d0e",
                            color: "#fff",
                            padding: "4px 6px",
                            borderRadius: 5,
                            fontWeight: 700,
                            fontSize: "0.69rem",
                            border: `1px solid ${CASINO.gold}`,
                            width: "100%",
                            cursor: (dealerReadyClicked || (readies && readies.includes(player.playerID))) ? "default" : "pointer",
                          }}
                        >
                          {!canReadyToDeal ? "Viewing..." : (dealerReadyClicked || (readies && readies.includes(player.playerID))) ? "Ready!" : "Ready to Deal"}
                        </button>
                      )}

                      {phase === "waiting" && isFirstHand && (
                        <button
                          disabled={readyList.includes(player.playerID)}
                          onClick={handleReadyForSuitVote}
                          style={{
                            background: readyList.includes(player.playerID) ? "#166534" : "#854d0e",
                            color: "#fff",
                            padding: "4px 6px",
                            borderRadius: 5,
                            fontWeight: 700,
                            fontSize: "0.69rem",
                            border: `1px solid ${CASINO.gold}`,
                            width: "100%",
                            cursor: readyList.includes(player.playerID) ? "default" : "pointer",
                          }}
                        >
                          {readyList.includes(player.playerID) ? "Ready!" : "Ready to Play"}
                        </button>
                      )}

                      {phase === "results" && (
                        <button
                          disabled={resultReadies.includes(player.playerID)}
                          onClick={handleNextRoundReady}
                          style={{
                            background: resultReadies.includes(player.playerID) ? "#166534" : "#854d0e",
                            color: "#fff",
                            padding: "4px 6px",
                            borderRadius: 5,
                            fontWeight: 700,
                            fontSize: "0.69rem",
                            border: `1px solid ${CASINO.gold}`,
                            width: "100%",
                            cursor: resultReadies.includes(player.playerID) ? "default" : "pointer",
                          }}
                        >
                          {resultReadies.includes(player.playerID) ? "Ready!" : "Next Round"}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </div>
        </div>
      </div>

      {(historyTab || paymentTab) && playerNeedsAction && (
        <div style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: showChat ? 300 : 0,
          zIndex: 2000,
          background: "#1e293b",
          color: "#fff",
          padding: "12px 16px",
          boxShadow: "0 -4px 20px rgba(0,0,0,0.25)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}>
          <div style={{ flex: 1, minWidth: 180, fontWeight: 600, fontSize: "0.9rem" }}>
            Action required — respond to continue the game
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {phase === "action" && mySeatIdx === activePlayerIdx && (
              <>
                <button onClick={handleKnock} style={{ background: "#166534", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>Knock</button>
                <button onClick={handleDiscard} style={{ background: "#7f1d1d", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>Fold</button>
              </>
            )}
            {phase === "dealer_selected" && dealerRevealFound != null && dealerRevealDone && (
              <button
                onClick={() => {
                  setDealerReadyClicked(true);
                  socket.emit("dealerSelectedReady", { tableID: table.id, playerID: player.playerID });
                }}
                disabled={!canReadyToDeal || (readies && readies.includes(player.playerID))}
                style={{ background: (readies && readies.includes(player.playerID)) ? "#166534" : "#854d0e", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}
              >
                {(readies && readies.includes(player.playerID)) ? "Ready!" : "Ready to Deal"}
              </button>
            )}
            {phase === "waiting" && isFirstHand && (
              <button
                disabled={readyList.includes(player.playerID)}
                onClick={handleReadyForSuitVote}
                style={{ background: readyList.includes(player.playerID) ? "#166534" : "#854d0e", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}
              >
                {readyList.includes(player.playerID) ? "Ready!" : "Ready to Play"}
              </button>
            )}
            {phase === "results" && (
              <button
                disabled={resultReadies.includes(player.playerID)}
                onClick={handleNextRoundReady}
                style={{ background: resultReadies.includes(player.playerID) ? "#166534" : "#854d0e", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}
              >
                {resultReadies.includes(player.playerID) ? "Ready!" : "Next Round"}
              </button>
            )}
            <button
              onClick={() => { setHistoryTab(false); setPaymentTab(false); }}
              style={{ background: "transparent", color: "#93c5fd", border: "1px solid #93c5fd", padding: "8px 16px", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}
            >
              Back to table
            </button>
          </div>
        </div>
      )}

      {spectators.length > 0 && (
        <div style={{
          background: COLORS.surface,
          borderRadius: "16px",
          padding: "20px",
          marginBottom: "24px",
          boxShadow: SHADOWS.md,
          border: `1px solid ${COLORS.border}`
        }}>
          <div style={{
            fontWeight: "700",
            fontSize: "1.125rem",
            marginBottom: "16px",
            color: COLORS.text
          }}>
            Spectators ({spectators.length})
          </div>
          <div style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px"
          }}>
            {spectators.map(s => (
              <div 
                key={s.playerID}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  background: "#f8fafc",
                  borderRadius: "20px",
                  border: `1px solid ${COLORS.border}`,
                  fontSize: "0.875rem"
                }}
              >
                <img 
                  src={displayAvatar(s)} 
                  alt="spectator" 
                  style={{
                    width: "18px",
                    height: "18px",
                    borderRadius: "50%",
                    border: `1px solid ${COLORS.border}`
                  }}
                />
                <span style={{
                  padding: "2px 8px",
                  background: "#fef3c7",
                  color: "#92400e",
                  borderRadius: "12px",
                  fontSize: "0.75rem",
                  fontWeight: "600"
                }}>
                  Spectator
                </span>
                <span style={{ 
                  fontSize: "0.875rem",
                  color: COLORS.text,
                  fontWeight: "500"
                }}>
                  {s.name}
                </span>
                {isHost && (
                  <button
                    title="Remove Spectator"
                    onClick={() => handleRemoveSpectator(s.playerID)}
                    style={{
                      background: "none", 
                      border: "none",
                      color: COLORS.danger, 
                      fontSize: "1rem",
                      cursor: "pointer", 
                      fontWeight: "700", 
                      lineHeight: "1",
                      padding: "2px",
                      borderRadius: "4px",
                      transition: ANIMATIONS.hover
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "#fef2f2";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "none";
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Side Chat Panel */}
      {showChat && (
        <div style={{
          position: "fixed",
          bottom: 0,
          right: 0,
          width: "320px",
          height: "50vh",
          background: COLORS.surface,
          borderLeft: `1px solid ${COLORS.border}`,
          borderTop: `1px solid ${COLORS.border}`,
          boxShadow: SHADOWS.lg,
          display: "flex",
          flexDirection: "column",
          zIndex: 1000
        }}>
          <div style={{
            padding: "16px 20px",
            borderBottom: `1px solid ${COLORS.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: COLORS.primary,
            color: "white"
          }}>
            <div style={{
              fontWeight: "700",
              fontSize: "1.125rem"
            }}>
              Table Chat
            </div>
            <button
              onClick={() => setShowChat(false)}
              style={{
                background: "none",
                border: "none",
                color: "white",
                fontSize: "1.5rem",
                cursor: "pointer",
                padding: "4px",
                borderRadius: "4px",
                transition: ANIMATIONS.hover
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "rgba(255,255,255,0.1)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "none";
              }}
            >
              ×
            </button>
          </div>
          
          <div style={{
            flex: 1,
            overflow: "auto",
            padding: "16px",
            background: "#f8fafc"
          }}>
            {chat && chat.length
              ? chat.map((msg, i) =>
                <div key={i}
                  style={{ 
                    marginBottom: 8,
                    padding: "8px 12px",
                    background: msg.system ? "#fef3c7" : COLORS.surface,
                    borderRadius: "8px",
                    border: `1px solid ${msg.system ? "#fbbf24" : COLORS.border}`,
                    fontSize: "0.875rem"
                  }}>
                  <div style={{ 
                    color: COLORS.textMuted, 
                    fontSize: "0.75rem",
                    marginBottom: "4px"
                  }}>
                    {msg.timestamp ? msg.timestamp.slice(11, 19) : ''}
                  </div>
                  {msg.system
                    ? <span style={{ color: "#92400e", fontWeight: "600" }}>{msg.text}</span>
                    : <span><strong style={{ color: COLORS.primary }}>{msg.name || "Player"}:</strong> {msg.text}</span>
                  }
                </div>)
              : <div style={{ 
                  color: COLORS.textMuted,
                  textAlign: "center",
                  padding: "32px",
                  fontSize: "0.875rem"
                }}>
                  No chat yet
                </div>
            }
            <div ref={chatListEnd} />
          </div>
          
          {isSpectate ? (
            <div style={{ 
              fontSize: "0.875rem", 
              color: COLORS.textMuted,
              textAlign: "center",
              padding: "12px",
              background: "#f8fafc",
              borderTop: `1px solid ${COLORS.border}`
            }}>
              Spectators cannot chat
            </div>
          ) : (
            <form 
              onSubmit={e => { e.preventDefault(); sendChat(); }} 
              style={{
                padding: "16px",
                borderTop: `1px solid ${COLORS.border}`,
                background: COLORS.surface
              }}
            >
              <div style={{
                display: "flex",
                gap: "8px"
              }}>
                <input
                  value={chatMsg}
                  onChange={e => setChatMsg(e.target.value)}
                  placeholder="Type a message..."
                  maxLength={120}
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: "8px",
                    fontSize: "0.875rem",
                    outline: "none",
                    transition: ANIMATIONS.hover
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = COLORS.primary;
                    e.target.style.boxShadow = `0 0 0 3px ${COLORS.primary}20`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = COLORS.border;
                    e.target.style.boxShadow = "none";
                  }}
                />
                <button 
                  type="submit" 
                  disabled={!chatMsg.trim()}
                  style={{
                    padding: "12px 16px",
                background: chatMsg.trim() ? COLORS.primary : COLORS.textMuted,
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontWeight: "600",
                fontSize: "0.875rem",
                cursor: chatMsg.trim() ? "pointer" : "not-allowed",
                transition: ANIMATIONS.hover
              }}
              onMouseEnter={(e) => {
                if (chatMsg.trim()) {
                  e.target.style.background = COLORS.primaryHover;
                  e.target.style.transform = "translateY(-1px)";
                }
              }}
              onMouseLeave={(e) => {
                if (chatMsg.trim()) {
                  e.target.style.background = COLORS.primary;
                  e.target.style.transform = "translateY(0)";
                }
              }}
            >
              Send
            </button>
               </div>
             </form>
           )}
         </div>
       )}

      {/* Footer */}
      <div style={{ 
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px 0"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {isSpectate && (
            <span style={{ 
              color: COLORS.textMuted, 
              fontWeight: '600',
              fontSize: "0.875rem",
              padding: "6px 12px",
              background: "#fef3c7",
              borderRadius: "20px",
              border: `1px solid #fbbf24`
            }}>
              Spectator Mode
            </span>
          )}
        </div>

      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000
        }}>
          <div style={{
            background: COLORS.surface,
            borderRadius: "16px",
            padding: "32px",
            maxWidth: "500px",
            width: "90%",
            boxShadow: SHADOWS.xl,
            border: `1px solid ${COLORS.border}`
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px"
            }}>
              <h3 style={{
                margin: 0,
                fontSize: "1.5rem",
                fontWeight: "700",
                color: COLORS.text
              }}>
                Share Invite Link
              </h3>
              <button
                onClick={() => setShowInviteModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.5rem",
                  cursor: "pointer",
                  color: COLORS.textMuted,
                  padding: "4px",
                  borderRadius: "4px",
                  transition: ANIMATIONS.hover
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#f1f5f9";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "none";
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ marginBottom: "20px" }}>
              <label style={{
                display: "block",
                fontWeight: "600",
                marginBottom: "8px",
                color: COLORS.text
              }}>
                Player Link
              </label>
              <div style={{
                display: "flex",
                gap: "8px"
              }}>
                <input
                  value={inviteLink}
                  readOnly
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: "8px",
                    fontSize: "0.875rem",
                    background: "#f8fafc"
                  }}
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(inviteLink);
                    // Could add a toast notification here
                  }}
                  style={{
                    padding: "12px 16px",
                    background: COLORS.primary,
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: ANIMATIONS.hover
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = COLORS.primaryHover;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = COLORS.primary;
                  }}
                >
                  Copy
                </button>
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{
                display: "block",
                fontWeight: "600",
                marginBottom: "8px",
                color: COLORS.text
              }}>
                Spectator Link
              </label>
              <div style={{
                display: "flex",
                gap: "8px"
              }}>
                <input
                  value={inviteLink + "&spectate=1"}
                  readOnly
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: "8px",
                    fontSize: "0.875rem",
                    background: "#f8fafc"
                  }}
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(inviteLink + "&spectate=1");
                    // Could add a toast notification here
                  }}
                  style={{
                    padding: "12px 16px",
                    background: COLORS.secondary,
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: ANIMATIONS.hover
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#475569";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = COLORS.secondary;
                  }}
                >
                  Copy
                </button>
              </div>
            </div>

            <div style={{
              padding: "16px",
              background: "#f0fdf4",
              borderRadius: "8px",
              border: `1px solid ${COLORS.success}20`
            }}>
              <div style={{
                fontSize: "0.875rem",
                color: COLORS.text,
                lineHeight: "1.5"
              }}>
                <strong>Table Code:</strong> <code style={{
                  background: "#eff6ff",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  fontFamily: "monospace",
                  color: COLORS.primary
                }}>{(tableCode || "").toUpperCase()}</code>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
